import { Component, Input, NgZone, OnDestroy } from "@angular/core";
import moment from 'moment';
import { Subscription } from "rxjs";
import { ImpersonationService } from "../../../../auth/apigee/impersonation.service";
import { LoggerService } from "../../../../common/logging/logger.service";
import { ModalDialogComponent } from "../../../../common/modal/modalDialog.component";
import { OfficeSelectorService } from "../../../../common/officeSelector/officeSelector.service";
import { OffersInNegotiationService, OffersInNegotiationServiceData } from "../../services/offersInNegotiationKpi/offersInNegotiation.service";
import { ColumnDefinition, TextColumnDefinition } from "../../../../dashboardCommon/components/cardDetails/datatable.component";
import { SingleSummaryDetailKpiServiceHost } from "../../../../dashboardCommon/components/kpiServiceHostComponent";

export class OffersInNegotiationDOData {
    public consultantOfferCount: number;
    public officeOfferCount: number;

    constructor(consultantOfferCount: number, officeOfferCount: number) {
        this.consultantOfferCount = consultantOfferCount;
        this.officeOfferCount = officeOfferCount;
    }
}

export class OffersInNegotiationDODetailData {
    public columnDefinitions: ColumnDefinition[];
    public tabledata: any[][];

    constructor(columnDefinitions: ColumnDefinition[], tabledata: any[][]) {
        this.columnDefinitions = columnDefinitions;
        this.tabledata = tabledata;
    }
}


@Component({
    selector: 'offers-in-negotiation-do-kpi-service-host',
    template: '<ng-content></ng-content>',
    standalone: false
})
export class OffersInNegotiationDOKpiServiceHostComponent extends SingleSummaryDetailKpiServiceHost<OffersInNegotiationService, OffersInNegotiationServiceData, OffersInNegotiationDOData, OffersInNegotiationDODetailData>
    implements OnDestroy {
    public getClassName() { return "OffersInNegotiationDOKpiServiceHostComponent"; }

    private raasSubscription: Subscription;
    private officeSelectorSubscription: Subscription;
    @Input() public detailDialog: ModalDialogComponent;
    @Input() public messageDialog: ModalDialogComponent;

    constructor(zone: NgZone, logger: LoggerService, private impersonationService: ImpersonationService, private officeSelectorService: OfficeSelectorService,
        summaryDetailService: OffersInNegotiationService) {
        super(zone, logger, summaryDetailService);
        this.raasSubscription = this.impersonationService.impersonationChanged.subscribe(() => { this.resetSummary(); });
        this.officeSelectorSubscription = this.officeSelectorService.selectedOfficeUpdated.subscribe(() => { this.resetSummary(); });
    }

    protected processSummaryData(data: OffersInNegotiationServiceData, updateRequested: Boolean): OffersInNegotiationDOData {
        let now = moment();
        let thisWeek = now.isoWeek();
        let timestamp = moment(data.offersInNegotiationUpdateTimestamp);
        if (timestamp.isoWeek() == thisWeek) {
            let diff = moment.duration(now.diff(timestamp));
            if (diff.hours() < 4 && this.summaryData && updateRequested) {
                this.messageDialog.open();
            }
        }

        let consultantOfferCount = data.consultantEntry ? data.consultantEntry.offersInNegotiationCount : 0;
        let officeOfferCount = data.entries.map((x) => x.offersInNegotiationCount).reduce((a, b) => a + b, 0);

        return new OffersInNegotiationDOData(consultantOfferCount, officeOfferCount);
    }

    protected getDefaultSummaryData(): OffersInNegotiationDOData {
        let data = new OffersInNegotiationDOData(0, 0);
        return data;
    }

    protected extractUpdateTime(data: OffersInNegotiationServiceData): Date {
        return data.offersInNegotiationUpdateTimestamp;
    }

    protected processDetailData(data: OffersInNegotiationServiceData, args: any): OffersInNegotiationDODetailData {
        if (args && args.filter && args.filter.wholeOffice) {
            let columnDefinitions = [
                new TextColumnDefinition("Razón social"),
                new TextColumnDefinition("Segmento"),
                new TextColumnDefinition("Fecha"),
                new TextColumnDefinition("Potencial"),
                new TextColumnDefinition("Margen"),
                new TextColumnDefinition("Consultor", null, true)
            ];

            let tabledata = [];
            let thisWeek = moment().isoWeek();
            for (let entry of (data.entries || [])) {
                let offers = (entry.offersInNegotiation || [])
                    .filter(x => moment(x.updateTimestamp).isoWeek() == thisWeek)
                    .sort(function (a, b) {
                        if (a.date == b.date) {
                            if (a.segment > b.segment) {
                                return 1;
                            }
                            if (a.segment < b.segment) {
                                return -1;
                            }
                            return 0;
                        }
                        else {
                            if (a.date > b.date) {
                                return 1;
                            }
                            if (a.date < b.date) {
                                return -1;
                            }
                            // a must be equal to b
                            return 0;
                        }
                    });

                for (let offer of offers) {
                    tabledata.push([
                        offer.trading_name,
                        "S" + offer.segment,
                        moment(offer.date).format("DD/MM/YYYY"),
                        offer.potential,
                        offer.min_margin,
                        entry.name
                    ]);
                }
            }

            return new OffersInNegotiationDODetailData(columnDefinitions, tabledata);
        } else {
            if (data.consultantEntry) {
                let columnDefinitions = [
                    new TextColumnDefinition("Razón social"),
                    new TextColumnDefinition("Segmento"),
                    new TextColumnDefinition("Fecha"),
                    new TextColumnDefinition("Potencial"),
                    new TextColumnDefinition("Margen")
                ];

                let tabledata = [];
                let thisWeek = moment().isoWeek();

                let offers = (data.consultantEntry.offersInNegotiation || [])
                    .filter(x => moment(x.updateTimestamp).isoWeek() == thisWeek);

                for (let offer of offers) {
                    tabledata.push([
                        offer.trading_name,
                        "S" + offer.segment,
                        moment(offer.date).format("DD/MM/YYYY"),
                        offer.potential,
                        offer.min_margin
                    ]);
                }

                tabledata = tabledata.sort(function (a, b) {
                    let dateA = a[2];
                    let dateB = b[2];
                    let segmentA = a[1];
                    let segmentB = b[1];
                    if (dateA == dateB) {
                        if (segmentA > segmentB) {
                            return 1;
                        }
                        if (segmentA < segmentB) {
                            return -1;
                        }
                        return 0;
                    }
                    else {
                        if (dateA > dateB) {
                            return 1;
                        }
                        if (dateA < dateB) {
                            return -1;
                        }
                        // a must be equal to b
                        return 0;
                    }
                })

                return new OffersInNegotiationDODetailData(columnDefinitions, tabledata);
            } else {
                return this.getDefaultDetailData();
            }
        }
    }

    protected getDefaultDetailData() {
        let columnDefinitions = [
            new TextColumnDefinition("Razón social"),
            new TextColumnDefinition("Segmento"),
            new TextColumnDefinition("Fecha"),
            new TextColumnDefinition("Potencial"),
            new TextColumnDefinition("Margen")
        ];

        return new OffersInNegotiationDODetailData(columnDefinitions, []);
    }

    protected performDetailAction(): void {
        if (this.detailDialog) this.detailDialog.open();
    }

    public ngOnDestroy(): void {
        if (this.raasSubscription) this.raasSubscription.unsubscribe();
        if (this.officeSelectorSubscription) this.officeSelectorSubscription.unsubscribe();
    }

    public mergeFilter(filter: any) {
        let detailProcessingArgs = this.detailProcessingArgs || {};
        detailProcessingArgs.filter = detailProcessingArgs.filter || {};
        detailProcessingArgs.filter = { ...detailProcessingArgs.filter, ...filter };
        this.detailProcessingArgs = detailProcessingArgs;
    }
}
